<div class="wrapper flex flex-col justify-between">
  <div class="heading flex flex-row items-center justify-between mb-[20px]">
    <div class="text-type-2">
      {{ data?.id || data?.uuid ? data?.name || 'Edit contractor' : 'Create contractor' }}
    </div>
  </div>

  @if (isEditMode) {
    <mat-tab-group class="contents-tab-group flex-1" animationDuration="0ms" (selectedIndexChange)="selectedTabIndex = $event">
      <mat-tab label="Details">
        <div class="pt-[20px]">
          <ng-container *ngTemplateOutlet="detailsForm" />
        </div>
      </mat-tab>
      <mat-tab label="Users">
        <div class="users-tab-content mt-[20px]">
          <!-- Assigned users list -->
          @if (contractorAccounts.length) {
            <div class="text-type-13 uppercase mb-[12px]">Assigned Users</div>
            <div class="user-list mb-[16px]">
              @for (account of contractorAccounts; track account.account_id) {
                <div class="user-item flex flex-row items-center justify-between py-[8px]">
                  <div class="flex flex-col">
                    <span class="text-type-8">{{ account.first_name }} {{ account.last_name }}</span>
                    <span class="text-type-14 text-[#60698B]">{{ account.email }}</span>
                  </div>
                  <div class="flex flex-row items-center gap-[8px]">
                    <span class="role-badge">{{ account.role }}</span>
                    <button (click)="removeContractorUser(account)" class="remove-user-btn">
                      <mat-icon svgIcon="trash" />
                    </button>
                  </div>
                </div>
                <hr class="hr-line" />
              }
            </div>
          } @else {
            <div class="text-type-14 text-[#60698B] mb-[16px]">No users assigned to this contractor.</div>
          }

          <!-- Add existing user dropdown -->
          @if (!showNewUserForm) {
            <app-dropdown
              (addNewClicked)="toggleNewUserForm()"
              [enableAddNew]="true"
              [enableSearch]="true"
              [enableMultiple]="true"
              [showCount]="true"
              [selectionString]="'users'"
              [addNewLabel]="'Add new contractor user'"
              [options]="memberOptions"
              [parentFormGroup]="userSelectForm"
              class="w-full mb-[20px]"
              controlName="selectedUser"
              label="select or add user"
              placeholder="Select"
            />
          }

          <!-- Add new user form -->
          @if (showNewUserForm) {
            <div class="text-type-13 uppercase mb-[12px]">Add New User</div>
            <app-text-input
              [parentFormGroup]="newUserForm"
              class="w-full mb-[20px]"
              controlName="firstName"
              label="first name"
            />
            <app-text-input
              [parentFormGroup]="newUserForm"
              class="w-full mb-[20px]"
              controlName="lastName"
              label="last name"
            />
            <app-text-input
              [parentFormGroup]="newUserForm"
              class="w-full mb-[20px]"
              controlName="email"
              label="email"
            />
            <app-text-input
              [parentFormGroup]="newUserForm"
              class="w-full mb-[20px]"
              controlName="phone"
              label="phone"
            />
            <div class="flex flex-row gap-[12px] mb-[20px]">
              <app-secondary-button (handleClick)="toggleNewUserForm()" class="flex-1">Cancel</app-secondary-button>
              <app-primary-button (handleClick)="addNewUser()" [isDisabled]="!newUserForm.valid" class="flex-1">Add</app-primary-button>
            </div>
          }
        </div>
      </mat-tab>
    </mat-tab-group>
  } @else {
    <ng-container *ngTemplateOutlet="detailsForm" />
  }

  @if (selectedTabIndex === 0) {
    <div class="action-section w-full flex justify-between items-center gap-[10px] mt-[40px]">
      <app-primary-button (handleClick)="submit()" [isDisabled]="!isFormDirty" class="w-full">
        Submit
      </app-primary-button>
    </div>
  }
</div>

<ng-template #detailsForm>
  <div class="details flex-1">
    <app-text-input
      (checkForErrors)="checkForErrors($event)"
      [error]="errorMessages['name']"
      [parentFormGroup]="form"
      class="w-full mb-[20px]"
      controlName="name"
      label="name"
    />
    <div class="flex flex-row justify-between items-center mb-[40px] gap-[20px] w-full">
      <div class="label text-type-13 uppercase">Prime</div>
      <mat-slide-toggle
        (change)="onToggle($event)"
        [checked]="form?.controls?.is_prime?.value"
        [disableRipple]="true"
        class="slide-toggle-btn"
        color="primary"
      />
    </div>
    <div class="flex flex-row justify-between items-center mb-[40px] gap-[20px] w-full">
      <div
        class="label text-type-13 uppercase"
        matTooltip="Disadvantaged / Minority / Women Business Enterprise"
        matTooltipPosition="above"
        matTooltipClass="project-name-tooltip"
      >D/M/WBE</div>
      <mat-slide-toggle
        (change)="onDmwbeToggle($event)"
        [checked]="form?.controls?.is_dmwbe?.value"
        [disableRipple]="true"
        class="slide-toggle-btn"
        color="primary"
      />
    </div>
    <app-text-input
      (checkForErrors)="checkForErrors($event)"
      [error]="errorMessages['poc']"
      [parentFormGroup]="form"
      class="w-full mb-[20px]"
      controlName="poc"
      label="POC"
    />
    <app-text-input
      (checkForErrors)="checkForErrors($event)"
      [error]="errorMessages['poc_phone']"
      [maxLength]="15"
      [parentFormGroup]="form"
      class="w-full mb-[20px]"
      controlName="poc_phone"
      label="POC phone"
      [placeholder]="'+1'"
    />

    <div class="address-section mt-[20px]">
      <div class="text-type-13 uppercase mb-[20px]">Address</div>
      <app-text-input
        (checkForErrors)="checkForErrors($event)"
        [error]="errorMessages['addressLine1']"
        [parentFormGroup]="form"
        class="w-full mb-[20px]"
        controlName="addressLine1"
        label="line 1"
      />
      <app-text-input
        (checkForErrors)="checkForErrors($event)"
        [error]="errorMessages['addressLine2']"
        [parentFormGroup]="form"
        class="w-full mb-[20px]"
        controlName="addressLine2"
        label="line 2"
      />
      <app-text-input
        (checkForErrors)="checkForErrors($event)"
        [error]="errorMessages['addressCity']"
        [parentFormGroup]="form"
        class="w-full mb-[20px]"
        controlName="addressCity"
        label="city"
      />
      <app-text-input
        (checkForErrors)="checkForErrors($event)"
        [error]="errorMessages['addressRegion']"
        [parentFormGroup]="form"
        class="w-full mb-[20px]"
        controlName="addressRegion"
        label="region"
      />
      <app-text-input
        (checkForErrors)="checkForErrors($event)"
        [error]="errorMessages['addressCountry']"
        [parentFormGroup]="form"
        class="w-full mb-[20px]"
        controlName="addressCountry"
        label="country"
      />
      <app-text-input
        (checkForErrors)="checkForErrors($event)"
        [error]="errorMessages['addressZip']"
        [parentFormGroup]="form"
        class="w-full mb-[20px]"
        controlName="addressZip"
        label="zip"
      />
    </div>
  </div>
</ng-template>
